// infrastructure/mappers/authorizationMapper.js
import { createAuthorization } from '../../domain/model/Authorization';

/**
 * Reads and writes the stored representation of an Authorization.
 *
 * NOT AN ANTI-CORRUPTION LAYER. An ACL defends a model from ANOTHER context's; the billing projector
 * is this same context in another runtime that cannot import from `src/`. So the persisted names are
 * a CONTRACT SHARED BY COPY, and nothing here may rename a field the projector reads. This file's
 * job is narrower: the domain says `clientId` where the rules helpers force `client`, and holds one
 * `period` where the document stores two flat bounds.
 *
 * THE CONTRACT, which the projector's own reader mirrors field for field:
 *
 *   collection      authorizations, a flat root collection
 *   scope           `client` + `clinic`, bare — the rules helpers read those exact names
 *   filter          `isDeleted == false`, mandatory; a tombstone read as live clears a hold
 *   identity        `authorizationId` — never the bare document id, see below
 *   payer           `fundingSourceId`, the SERIES key, never a version document
 *   dates           `periodStart` / `periodEnd`, 'YYYY-MM-DD', inclusive, never Timestamps
 *   match keys      `limits[].services[].treatmentServiceId`, `limits[].placeOfServiceCodes`
 *
 * A FIELD RENAMED ON ONE SIDE DOES NOT THROW ON THE OTHER. It produces a gate that matches nothing,
 * reading as "no authorization on file" — a hold that looks like missing data rather than a fault.
 *
 * MAPPED FIELD BY FIELD, NEVER SPREAD. A spread would carry the document id, the audit stamps and
 * anything a later write adds straight into the model.
 *
 * `periodStart` / `periodEnd`, NOT `effectiveStart` / `effectiveEnd`, unlike `clientCoverages`. A
 * coverage has ONE date axis; an authorization will have two, and reserving `effectiveFrom` now
 * avoids a correctness-bug migration when a reader wiring version-dating would otherwise find the
 * window sitting on the name it wanted.
 */

/**
 * Model limits → stored limits.
 *
 * `resetBehavior` IS WRITTEN, ALWAYS NULL TODAY. No control ships in this bet, but the field is
 * written anyway because adding one to a nested cap later means rewriting every stored cap. Null
 * says the behavior was not recorded, which lets consumption HOLD rather than assume.
 *
 * @param {import('../../domain/model/ApprovedLimit').ApprovedLimit[]} limits
 * @returns {Object[]}
 */
const toStoredLimits = (limits) => {
  return (limits ?? []).map((limit) => ({
    limitId: limit.limitId,
    services: limit.services.map((service) => ({
      treatmentServiceId: service.treatmentServiceId,
    })),
    placeOfServiceCodes: limit.placeOfServiceCodes,
    caps: limit.caps.map((cap) => ({
      figure: cap.figure,
      statedUnit: cap.statedUnit,
      period: cap.period,
      resetBehavior: cap.resetBehavior,
    })),
  }));
};

/**
 * Firestore document → Authorization.
 *
 * `authorizationId` FALLS BACK TO THE DOCUMENT ID — the seam, not a repair. The two are the same
 * string today, and reading through this field is what makes versioning additive: the day a second
 * document exists for one approval, every reader that went through `doc.id` would silently start
 * pointing at a version.
 *
 * @param {Object} storedAuthorization - the stored document, including its `id`
 * @returns {import('../../domain/model/Authorization.types').Authorization}
 */
export function toAuthorization(storedAuthorization) {
  return createAuthorization({
    authorizationId: storedAuthorization?.authorizationId ?? storedAuthorization?.id ?? null,
    clientId: storedAuthorization?.client ?? null,
    clinicId: storedAuthorization?.clinic ?? null,
    fundingSourceId: storedAuthorization?.fundingSourceId ?? null,
    authorizationNumber: storedAuthorization?.authorizationNumber ?? null,
    periodStart: storedAuthorization?.periodStart,
    periodEnd: storedAuthorization?.periodEnd,
    limits: storedAuthorization?.limits,
    isDeleted: storedAuthorization?.isDeleted === true,
  });
} // toAuthorization()

/** Firestore documents → Authorization[], in the order given. */
export function toAuthorizations(documents) {
  return (documents ?? []).map(toAuthorization);
} // toAuthorizations()

/**
 * Authorization → document fields, without the id.
 *
 * WRITES THE SCOPING KEYS BUT NOT THE TOMBSTONE STAMPS. `isDeleted` is a fact every read filters on;
 * `deleteTimestamp` and `deletedBy` belong to the moment of a write, and the domain reads no clock.
 *
 * `clinic` IS THE CLIENT'S, NOT THE WRITER'S — passed through from the aggregate, resolved by the
 * use case, and validated independently by the rules.
 *
 * @param {import('../../domain/model/Authorization.types').Authorization} authorization
 * @returns {Object}
 */
export function toDocument(authorization) {
  return {
    authorizationId: authorization.authorizationId,
    client: authorization.clientId,
    clinic: authorization.clinicId,
    fundingSourceId: authorization.fundingSourceId,
    authorizationNumber: authorization.authorizationNumber,
    periodStart: authorization.period.start,
    periodEnd: authorization.period.end,
    limits: toStoredLimits(authorization.limits),
    isDeleted: authorization.isDeleted === true,
  };
} // toDocument()
